/**
 * Events Roll Desk Section
 * Auto-rolling events with configurable spotlights and click-to-view
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar, Clock, MapPin, Navigation, Ticket, ChevronLeft, ChevronRight, PartyPopper, Music, Building, Trophy, Users, Sparkles, X, Phone, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'all', label: 'All Events', icon: Sparkles },
  { value: 'party', label: 'Parties', icon: PartyPopper },
  { value: 'concert', label: 'Concerts', icon: Music },
  { value: 'festival', label: 'Festivals', icon: Sparkles },
  { value: 'corporate', label: 'Corporate', icon: Building },
  { value: 'sports', label: 'Sports', icon: Trophy },
  { value: 'community', label: 'Community', icon: Users },
];

const PARISHES = [
  'all', 'kingston', 'st. andrew', 'st. catherine', 'st. thomas', 
  'clarendon', 'st. ann', 'st. mary', 'portland', 'st. james',
  'hanover', 'westmoreland', 'trelawny', 'manchester', 'st. elizabeth'
];

const COLOR_CLASSES = {
  yellow: { from: 'from-yellow-400', via: 'via-yellow-300', bg: 'bg-yellow-400' },
  pink: { from: 'from-pink-400', via: 'via-pink-300', bg: 'bg-pink-400' },
  purple: { from: 'from-purple-400', via: 'via-purple-300', bg: 'bg-purple-400' },
  blue: { from: 'from-blue-400', via: 'via-blue-300', bg: 'bg-blue-400' },
  green: { from: 'from-green-400', via: 'via-green-300', bg: 'bg-green-400' },
  red: { from: 'from-red-400', via: 'via-red-300', bg: 'bg-red-400' },
  orange: { from: 'from-orange-400', via: 'via-orange-300', bg: 'bg-orange-400' },
  cyan: { from: 'from-cyan-400', via: 'via-cyan-300', bg: 'bg-cyan-400' },
};

export const EventsRollSection = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedParish, setSelectedParish] = useState('all');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [settings, setSettings] = useState({
    card_size_percent: 70,
    auto_scroll_speed: 5,
    show_searchlights: true,
    light_count: 2,
    light_color_1: 'yellow',
    light_color_2: 'pink',
    light_color_3: 'purple',
    light_color_4: 'blue',
    light_brightness: 80,
    light_length: 520
  });

  useEffect(() => {
    fetchEvents();
    fetchSettings();
  }, [selectedCategory, selectedParish]);

  useEffect(() => {
    if (events.length <= 1 || isPaused) return;
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % events.length);
    }, settings.auto_scroll_speed * 1000);
    return () => clearInterval(interval);
  }, [events.length, isPaused, settings.auto_scroll_speed]);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/events/display-settings`);
      if (res.ok) {
        const data = await res.json();
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchEvents = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (selectedParish !== 'all') params.append('parish', selectedParish);
      params.append('limit', '10');
      
      const res = await fetch(`${API_URL}/api/events/public?${params}`);
      if (res.ok) {
        const data = await res.json();
        setEvents(data.events || []);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const openGoogleMaps = (event) => {
    if (event.venue_lat && event.venue_lng) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${event.venue_lat},${event.venue_lng}`, '_blank');
    } else {
      const encoded = encodeURIComponent(`${event.venue_name}, ${event.venue_address}, Jamaica`);
      window.open(`https://www.google.com/maps/search/?api=1&query=${encoded}`, '_blank');
    }
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-JM', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const handleInteraction = (newIndex) => {
    setIsPaused(true);
    setCurrentIndex(newIndex);
    setTimeout(() => setIsPaused(false), 10000);
  };

  const getCategoryIcon = (category) => {
    const cat = CATEGORIES.find(c => c.value === category);
    return cat ? cat.icon : PartyPopper;
  };

  const getColorClass = (colorName) => COLOR_CLASSES[colorName] || COLOR_CLASSES.yellow;

  // Helper to get beam color with opacity for inline styles
  const getBeamColor = (colorName, opacity) => {
    const colors = {
      yellow: '250, 204, 21',   // yellow-400
      pink: '244, 114, 182',    // pink-400
      purple: '192, 132, 252',  // purple-400
      blue: '96, 165, 250',     // blue-400
      green: '74, 222, 128',    // green-400
      red: '248, 113, 113',     // red-400
      orange: '251, 146, 60',   // orange-400
      cyan: '34, 211, 238',     // cyan-400
    };
    const rgb = colors[colorName] || colors.yellow;
    return `rgba(${rgb}, ${opacity / 100})`;
  };

  if (loading) {
    return (
      <div className="py-8 px-4">
        <div className="animate-pulse bg-slate-800/50 rounded-2xl h-64" />
      </div>
    );
  }

  if (events.length === 0) {
    return null;
  }

  const brightness = settings.light_brightness;
  const beamLength = settings.light_length;

  return (
    <section className="py-8 px-4 md:px-8 relative overflow-hidden" data-testid="events-roll-section">
      <div className="max-w-7xl mx-auto relative">
        {/* Spotlight Instruments */}
        {settings.show_searchlights && (
          <>
            {/* Top-left spotlight */}
            {settings.light_count >= 1 && (
              <div className="absolute -top-4 -left-4 z-20">
                <div className="w-12 h-12 bg-gradient-to-br from-slate-600 to-slate-800 rounded-full border-2 border-slate-500 shadow-lg flex items-center justify-center">
                  <div className={`w-6 h-6 rounded-full shadow-inner ${getColorClass(settings.light_color_1).bg}`} />
                </div>
                <div 
                  className="absolute top-6 left-6 h-2 blur-sm origin-left animate-beam-sweep-1"
                  style={{ 
                    width: `${beamLength}px`,
                    background: `linear-gradient(to right, ${getBeamColor(settings.light_color_1, brightness)}, ${getBeamColor(settings.light_color_1, brightness/2)}, transparent)`
                  }}
                />
              </div>
            )}
            
            {/* Top-right spotlight */}
            {settings.light_count >= 2 && (
              <div className="absolute -top-4 -right-4 z-20">
                <div className="w-12 h-12 bg-gradient-to-br from-slate-600 to-slate-800 rounded-full border-2 border-slate-500 shadow-lg flex items-center justify-center">
                  <div className={`w-6 h-6 rounded-full shadow-inner ${getColorClass(settings.light_color_2).bg}`} />
                </div>
                <div 
                  className="absolute top-6 right-6 h-2 blur-sm origin-right animate-beam-sweep-2"
                  style={{ 
                    width: `${beamLength}px`,
                    background: `linear-gradient(to left, ${getBeamColor(settings.light_color_2, brightness)}, ${getBeamColor(settings.light_color_2, brightness/2)}, transparent)`
                  }}
                />
              </div>
            )}

            {/* Bottom-left spotlight */}
            {settings.light_count >= 3 && (
              <div className="absolute -bottom-4 -left-4 z-20">
                <div className="w-10 h-10 bg-gradient-to-br from-slate-600 to-slate-800 rounded-full border-2 border-slate-500 shadow-lg flex items-center justify-center">
                  <div className={`w-5 h-5 rounded-full shadow-inner ${getColorClass(settings.light_color_3).bg}`} />
                </div>
                <div 
                  className="absolute bottom-5 left-5 h-2 blur-sm origin-left animate-beam-sweep-3"
                  style={{ 
                    width: `${Math.floor(beamLength * 0.8)}px`,
                    background: `linear-gradient(to right, ${getBeamColor(settings.light_color_3, brightness)}, ${getBeamColor(settings.light_color_3, brightness/2)}, transparent)`
                  }}
                />
              </div>
            )}

            {/* Bottom-right spotlight */}
            {settings.light_count >= 4 && (
              <div className="absolute -bottom-4 -right-4 z-20">
                <div className="w-10 h-10 bg-gradient-to-br from-slate-600 to-slate-800 rounded-full border-2 border-slate-500 shadow-lg flex items-center justify-center">
                  <div className={`w-5 h-5 rounded-full shadow-inner ${getColorClass(settings.light_color_4).bg}`} />
                </div>
                <div 
                  className="absolute bottom-5 right-5 h-2 blur-sm origin-right animate-beam-sweep-4"
                  style={{ 
                    width: `${Math.floor(beamLength * 0.8)}px`,
                    background: `linear-gradient(to left, ${getBeamColor(settings.light_color_4, brightness)}, ${getBeamColor(settings.light_color_4, brightness/2)}, transparent)`
                  }}
                />
              </div>
            )}
          </>
        )}

        {/* Header Box */}
        <div className="relative mb-6">
          <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 via-pink-600 via-yellow-500 via-green-500 to-purple-600 rounded-2xl opacity-75 blur-sm animate-gradient-x" />
          <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500 via-pink-500 via-yellow-400 via-green-400 to-purple-500 rounded-2xl animate-gradient-x" />
          
          <div className="relative bg-slate-900/95 rounded-xl p-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="absolute -inset-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full blur animate-pulse" />
                  <div className="relative p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl">
                    <PartyPopper className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-yellow-400 animate-gradient-x">
                    Events in Jamaica
                  </h2>
                  <p className="text-gray-400 text-sm">Parties, concerts & more happening near you</p>
                </div>
              </div>
              
              <Button 
                size="sm"
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white text-sm px-4"
                onClick={() => navigate('/events/create')}
              >
                <Sparkles className="h-3 w-3 mr-1" />
                Post Your Event
              </Button>
            </div>

            <div className="flex flex-wrap gap-2 mt-3">
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-32 h-8 text-xs bg-slate-800/80 border-purple-500/30 text-white">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value} className="text-white text-xs">
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedParish} onValueChange={setSelectedParish}>
                <SelectTrigger className="w-32 h-8 text-xs bg-slate-800/80 border-pink-500/30 text-white">
                  <SelectValue placeholder="Parish" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {PARISHES.map(p => (
                    <SelectItem key={p} value={p} className="text-white text-xs capitalize">
                      {p === 'all' ? 'All Parishes' : p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Events Carousel */}
        <div className="relative" style={{ transform: `scale(${settings.card_size_percent / 100})`, transformOrigin: 'top center' }}>
          {events.length > 1 && (
            <>
              <button 
                onClick={() => handleInteraction((currentIndex - 1 + events.length) % events.length)}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 z-10 p-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full text-white hover:scale-110 transition-transform shadow-lg"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button 
                onClick={() => handleInteraction((currentIndex + 1) % events.length)}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 z-10 p-2 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full text-white hover:scale-110 transition-transform shadow-lg"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}

          <div className="overflow-hidden rounded-xl cursor-pointer">
            <div 
              className="flex transition-transform duration-700 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {events.map((event, idx) => {
                const CategoryIcon = getCategoryIcon(event.category);
                return (
                  <div 
                    key={event.id || idx} 
                    className="w-full flex-shrink-0"
                    onClick={() => { setIsPaused(true); setSelectedEvent(event); }}
                  >
                    <Card className="bg-gradient-to-br from-slate-800/90 to-slate-900/90 border-purple-500/30 overflow-hidden hover:border-purple-400/50 transition-colors">
                      <CardContent className="p-0">
                        <div className="flex flex-col md:flex-row">
                          <div className="md:w-1/3 h-44 md:h-auto relative">
                            {event.poster_url ? (
                              <img 
                                src={event.poster_url.startsWith('http') ? event.poster_url : `${API_URL}${event.poster_url}`}
                                alt={event.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center">
                                <CategoryIcon className="h-16 w-16 text-white/50" />
                              </div>
                            )}
                            <div className="absolute top-2 left-2 flex gap-1">
                              <Badge className="bg-purple-500/90 text-xs">{formatDate(event.event_date).split(',')[0]}</Badge>
                              <Badge className="bg-pink-500/90 text-xs capitalize">{event.category || 'party'}</Badge>
                            </div>
                            <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 hover:opacity-100">
                              <span className="text-white text-sm font-medium bg-black/50 px-3 py-1 rounded">Click for details</span>
                            </div>
                          </div>

                          <div className="md:w-2/3 p-4 flex flex-col justify-between">
                            <div>
                              <h3 className="text-lg font-bold text-white mb-1">{event.title}</h3>
                              {event.description && (
                                <p className="text-gray-400 text-xs mb-2 line-clamp-2">{event.description}</p>
                              )}
                              <div className="space-y-1 text-sm">
                                <div className="flex items-center gap-2 text-gray-300">
                                  <Calendar className="h-3 w-3 text-purple-400" />
                                  <span className="text-xs">{formatDate(event.event_date)}</span>
                                  <Clock className="h-3 w-3 text-purple-400 ml-2" />
                                  <span className="text-xs">{event.event_time}</span>
                                </div>
                                <div className="flex items-center gap-2 text-gray-300">
                                  <MapPin className="h-3 w-3 text-pink-400" />
                                  <span className="text-xs">{event.venue_name}</span>
                                </div>
                              </div>
                            </div>

                            <div className="flex flex-wrap gap-2 mt-3">
                              <Button 
                                size="sm"
                                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs h-8"
                                onClick={(e) => { e.stopPropagation(); openGoogleMaps(event); }}
                              >
                                <Navigation className="h-3 w-3 mr-1" />
                                Live Tracking
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                );
              })}
            </div>
          </div>

          {events.length > 1 && (
            <div className="flex justify-center gap-1.5 mt-3">
              {events.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => handleInteraction(idx)}
                  className={`h-2 rounded-full transition-all ${
                    idx === currentIndex 
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 w-6' 
                      : 'bg-slate-600 hover:bg-slate-500 w-2'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Full Event Detail Modal */}
      <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
        <DialogContent className="bg-slate-900 border-purple-500/30 max-w-2xl max-h-[90vh] overflow-y-auto p-0">
          {selectedEvent && (
            <>
              {selectedEvent.poster_url && (
                <div className="relative h-64 w-full">
                  <img 
                    src={selectedEvent.poster_url.startsWith('http') ? selectedEvent.poster_url : `${API_URL}${selectedEvent.poster_url}`}
                    alt={selectedEvent.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent" />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-2 right-2 text-white bg-black/50 hover:bg-black/70"
                    onClick={() => setSelectedEvent(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
              <div className="p-6">
                <div className="flex gap-2 mb-3">
                  <Badge className="bg-purple-500">{selectedEvent.category || 'Party'}</Badge>
                  <Badge variant="outline" className="border-pink-500/50 text-pink-300 capitalize">{selectedEvent.venue_parish}</Badge>
                </div>
                
                <h2 className="text-2xl font-bold text-white mb-2">{selectedEvent.title}</h2>
                
                {selectedEvent.description && (
                  <p className="text-gray-400 mb-4">{selectedEvent.description}</p>
                )}

                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3 text-gray-300">
                    <Calendar className="h-5 w-5 text-purple-400" />
                    <span>{formatDate(selectedEvent.event_date)}</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Clock className="h-5 w-5 text-purple-400" />
                    <span>{selectedEvent.event_time}</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <MapPin className="h-5 w-5 text-pink-400" />
                    <div>
                      <p className="font-medium">{selectedEvent.venue_name}</p>
                      <p className="text-sm text-gray-500">{selectedEvent.venue_address}</p>
                    </div>
                  </div>
                  {selectedEvent.ticket_price > 0 && (
                    <div className="flex items-center gap-3 text-gray-300">
                      <Ticket className="h-5 w-5 text-green-400" />
                      <span>J${selectedEvent.ticket_price.toLocaleString()}</span>
                    </div>
                  )}
                  {selectedEvent.contact_phone && (
                    <div className="flex items-center gap-3 text-gray-300">
                      <Phone className="h-5 w-5 text-blue-400" />
                      <span>{selectedEvent.contact_phone}</span>
                    </div>
                  )}
                  {selectedEvent.contact_email && (
                    <div className="flex items-center gap-3 text-gray-300">
                      <Mail className="h-5 w-5 text-blue-400" />
                      <span>{selectedEvent.contact_email}</span>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-3">
                  <Button 
                    className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white flex-1"
                    onClick={() => openGoogleMaps(selectedEvent)}
                  >
                    <Navigation className="h-4 w-4 mr-2" />
                    Get Directions (Live Tracking)
                  </Button>
                  {selectedEvent.ticket_link && (
                    <Button 
                      variant="outline"
                      className="border-green-500/50 text-green-300 flex-1"
                      onClick={() => window.open(selectedEvent.ticket_link, '_blank')}
                    >
                      <Ticket className="h-4 w-4 mr-2" />
                      Buy Tickets
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <style jsx global>{`
        @keyframes gradient-x {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes beam-sweep-1 {
          0%, 100% { transform: rotate(-20deg); opacity: 0.6; }
          50% { transform: rotate(40deg); opacity: 0.9; }
        }
        @keyframes beam-sweep-2 {
          0%, 100% { transform: rotate(20deg); opacity: 0.6; }
          50% { transform: rotate(-40deg); opacity: 0.9; }
        }
        @keyframes beam-sweep-3 {
          0%, 100% { transform: rotate(-15deg); opacity: 0.5; }
          50% { transform: rotate(35deg); opacity: 0.8; }
        }
        @keyframes beam-sweep-4 {
          0%, 100% { transform: rotate(15deg); opacity: 0.5; }
          50% { transform: rotate(-35deg); opacity: 0.8; }
        }
        .animate-gradient-x {
          background-size: 200% 200%;
          animation: gradient-x 3s ease infinite;
        }
        .animate-beam-sweep-1 { animation: beam-sweep-1 3s ease-in-out infinite; }
        .animate-beam-sweep-2 { animation: beam-sweep-2 4s ease-in-out infinite; }
        .animate-beam-sweep-3 { animation: beam-sweep-3 3.5s ease-in-out infinite; }
        .animate-beam-sweep-4 { animation: beam-sweep-4 4.5s ease-in-out infinite; }
      `}</style>

      {/* Tickets Coming Soon Banner */}
      {settings.show_tickets_banner && (
        <div className="relative mt-6 overflow-hidden">
          {/* Animated background */}
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-pink-500 via-yellow-400 via-green-400 to-blue-500 animate-gradient-flow" />
          
          {/* Sparkle overlay */}
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2220%22 height=%2220%22%3E%3Ccircle cx=%2210%22 cy=%2210%22 r=%221%22 fill=%22white%22 opacity=%220.5%22/%3E%3C/svg%3E')] animate-sparkle" />
          
          {/* Banner content */}
          <div className="relative py-4 px-6">
            <div className="flex items-center justify-center gap-4 flex-wrap">
              {/* Flashing ticket icons */}
              <span className="text-3xl animate-bounce-flash">🎫</span>
              
              {/* Main text with glow */}
              <div className="text-center">
                <h3 className="text-2xl md:text-3xl font-black text-white drop-shadow-[0_0_10px_rgba(255,255,255,0.8)] animate-pulse-glow tracking-wide">
                  {settings.tickets_banner_text || 'EVENT TICKETS COMING SOON!'}
                </h3>
                <p className="text-white/90 text-sm mt-1 font-medium">
                  Buy tickets directly through Liantiao • Secure payments • Instant delivery
                </p>
              </div>
              
              {/* Flashing ticket icons */}
              <span className="text-3xl animate-bounce-flash-delay">🎟️</span>
            </div>
          </div>
          
          {/* Animated border lights */}
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-white to-transparent animate-border-flow" />
          <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-white to-transparent animate-border-flow-reverse" />
        </div>
      )}
      
      <style jsx global>{`
        @keyframes gradient-flow {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes sparkle {
          0%, 100% { opacity: 0.3; transform: translateY(0); }
          50% { opacity: 0.7; transform: translateY(-5px); }
        }
        @keyframes pulse-glow {
          0%, 100% { text-shadow: 0 0 10px rgba(255,255,255,0.8), 0 0 20px rgba(255,255,255,0.6), 0 0 30px rgba(255,255,255,0.4); }
          50% { text-shadow: 0 0 20px rgba(255,255,255,1), 0 0 40px rgba(255,255,255,0.8), 0 0 60px rgba(255,255,255,0.6); }
        }
        @keyframes bounce-flash {
          0%, 100% { transform: translateY(0) scale(1); }
          25% { transform: translateY(-8px) scale(1.1); }
          50% { transform: translateY(0) scale(1); }
          75% { transform: translateY(-4px) scale(1.05); }
        }
        @keyframes bounce-flash-delay {
          0%, 100% { transform: translateY(0) scale(1); }
          25% { transform: translateY(-4px) scale(1.05); }
          50% { transform: translateY(-8px) scale(1.1); }
          75% { transform: translateY(0) scale(1); }
        }
        @keyframes border-flow {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes border-flow-reverse {
          0% { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
        .animate-gradient-flow {
          background-size: 400% 400%;
          animation: gradient-flow 4s ease infinite;
        }
        .animate-sparkle {
          animation: sparkle 2s ease-in-out infinite;
        }
        .animate-pulse-glow {
          animation: pulse-glow 1.5s ease-in-out infinite;
        }
        .animate-bounce-flash {
          animation: bounce-flash 1s ease-in-out infinite;
        }
        .animate-bounce-flash-delay {
          animation: bounce-flash-delay 1s ease-in-out infinite;
        }
        .animate-border-flow {
          animation: border-flow 2s linear infinite;
        }
        .animate-border-flow-reverse {
          animation: border-flow-reverse 2s linear infinite;
        }
      `}</style>
    </section>
  );
};

export default EventsRollSection;
